<template>
  <v-card flat class="notifications-bar">
    <v-app-bar elevation="1" class="px-11">
      <v-app-bar-title class="text-primary text-h6 font-weight-bold">
        Notifications
      </v-app-bar-title>
      <v-row>
        <v-col>
          <SiteAutocomplete
            class="mt-6 mr-5"
            :items="sitesList"
            v-model="selectedSite"
            @update:modelValue="
              (selectedSite) => {
                setDefaultSiteFromNotificationsDomain(selectedSite);
              }
            "
          />
        </v-col>
        <v-col>
          <v-text-field
            class="mt-6 mr-5"
            v-model="searchTerm"
            hide-details
            prepend-inner-icon="mdi-magnify"
            bg-color="white"
            single-line
            variant="outlined"
            density="compact"
            :placeholder="placeholder"
            :clearable="true"
            @click:clear="handleSearchInput"
            @update:modelValue="handleSearchInput"
          />
        </v-col>
      </v-row>

      <v-btn
        class="rounded-2 mr-2"
        color="primary"
        variant="flat"
        size="large"
        @click="handleBackToPortfolio"
      >
        <div class="text-subtitle-2 text-white text-truncate font-weight-bold">
          Back to My Portfolio
        </div>
      </v-btn>
      <v-btn
        class="rounded-2"
        color="accentOrange"
        variant="flat"
        size="large"
        @click="handleMarkAllAsRead"
        :loading="isMarkAllLoading"
      >
        <div class="text-subtitle-2 text-white text-truncate font-weight-bold">
          Mark All as Read
        </div>
      </v-btn>
      <v-spacer />
    </v-app-bar>
    <ProgressLinearLoaderVue :isLoading="isLoading" />
  </v-card>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';

import ProgressLinearLoaderVue from '@/Core.UI.Domain/Components/ProgressLinearLoader.vue';
import SiteAutocomplete from '@/Core.UI.Domain/Components/SiteAutocomplete.vue';

import eventBus from '@/Core.Service.Domain/Shared/eventBus.js';

export default {
  name: 'NotificationsBar',

  components: {
    ProgressLinearLoaderVue,
    SiteAutocomplete,
  },

  data() {
    return {
      searchTerm: '',
      selectedSite: null,
      siteId: null,
      isMarkAllLoading: false,
    };
  },

  async mounted() {
    try {
      if (this.sitesList.length === 0) {
        await this.getPortfolioSites();
      }
      if (!this.defaultSite) {
        await this.setDefaultSite(this.sitesList[0]);
      }
      this.setDefaultSiteFromNotificationsDomain(this.defaultSite);
    } catch (error) {
      return Promise.reject(error);
    }

    eventBus.$on('isMarkAllLoading', (bool) => {
      this.isMarkAllLoading = bool;
    });
  },

  computed: {
    ...mapGetters({
      defaultSite: 'session/defaultSite',
      isLoading:
        'ViewSupportStore/NotificationsStore/getNotificationsIsLoading',
      sitesList: 'ViewSupportStore/NotificationsStore/getSitesList',
    }),

    placeholder() {
      return this.$route.meta.placeholder || 'Search for a Notification';
    },
  },

  watch: {
    $route() {
      this.setDefaultSiteFromNotificationsDomain(this.defaultSite);
      this.clearSearchTerm();
    },

    sitesList: {
      handler() {
        if (this.sitesList.length > 0) {
          this.setDefaultSiteFromNotificationsDomain(this.defaultSite);
        }
      },
      immediate: true,
    },
  },

  methods: {
    ...mapActions({
      getNotificationsBySiteId:
        'ViewSupportStore/NotificationsStore/getNotificationsBySiteId',
      setDefaultSite: 'session/setDefaultSite',
      getPortfolioSites: 'ViewSupportStore/NotificationsStore/setSitesList',
    }),

    ...mapMutations({
      setNotificationsSearchTerm:
        'ViewSupportStore/NotificationsStore/NOTIFICATIONS_SEARCH_TERM',
    }),

    setDefaultSiteFromNotificationsDomain(selectedSite = this.defaultSite) {
      // Called without a site from mounted/$route/sitesList while there is no
      // default yet; the dropdown itself only ever hands over a real site.
      selectedSite = selectedSite || this.defaultSite;
      if (!selectedSite) {
        return;
      }
      const siteId = selectedSite.siteId || selectedSite.id;
      const selectedSiteId = this.$route.params.siteId || siteId;

      this.selectedSite = this.sitesList.find(
        (site) => site.siteId === Number(selectedSiteId)
      );

      this.getNotificationsBySiteId(siteId).then(() => {
        this.setDefaultSite(this.selectedSite);
        this.clearSearchTerm();
        this.$router
          .push({
            name: 'AllNotifications',
            params: { siteId: this.siteId },
          })
          .catch(() => {});
      });
    },

    handleSearchInput(value) {
      if (typeof value === 'object') {
        this.setNotificationsSearchTerm('');
      } else {
        this.setNotificationsSearchTerm(value);
      }
    },

    clearSearchTerm() {
      this.searchTerm = '';
      this.setNotificationsSearchTerm('');
    },

    handleBackToPortfolio() {
      this.$router.push({ name: 'Portfolio' });
    },

    handleMarkAllAsRead() {
      eventBus.$emit('markAllAsRead');
    },
  },

  beforeRouteUpdate(to) {
    this.siteId = to.params.siteId;
    return true;
  },
};
</script>
<style lang="scss" scoped>
.notifications-bar {
  z-index: 1;
}

.v-input {
  max-width: 700px !important;
}

:deep(.v-field__input) {
  color: $primary !important;
}

:deep(.bg-white) {
  color: $primary !important;
}
</style>
